import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ScrollLink } from "@/components/ui/scroll-link";
import { Search as SearchIcon } from "lucide-react";
import { useBooks, useAuthors, useBitcoiners } from "@/hooks/use-sheets-data";
import { cn } from "@/lib/utils";

interface SearchProps {
  placeholder?: string;
  className?: string;
}

export function Search({ placeholder, className }: SearchProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const { data: books = [] } = useBooks();
  const { data: authors = [] } = useAuthors();
  const { data: bitcoiners = [] } = useBitcoiners();

  const filteredBooks = books.filter(book => 
    book.title?.toLowerCase().includes(query.toLowerCase()) ||
    book.description?.toLowerCase().includes(query.toLowerCase())
  ).slice(0, 3);

  const filteredAuthors = authors.filter(author =>
    author.name?.toLowerCase().includes(query.toLowerCase()) ||
    author.bio?.toLowerCase().includes(query.toLowerCase())
  ).slice(0, 3);

  const filteredBitcoiners = bitcoiners.filter(bitcoiner =>
    bitcoiner.name?.toLowerCase().includes(query.toLowerCase()) ||
    bitcoiner.bio?.toLowerCase().includes(query.toLowerCase())
  ).slice(0, 3);

  return (
    <>
      <div 
        onClick={() => setOpen(true)}
        className={cn(
          "flex items-center rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background cursor-text",
          className
        )}
      >
        <SearchIcon className="h-4 w-4 mr-2 text-muted-foreground" />
        <span className="text-muted-foreground">{placeholder}</span>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[600px] p-0">
          <div className="p-3 border-b">
            <div className="flex items-center gap-2">
              <SearchIcon className="h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder={placeholder}
                className="border-none focus-visible:ring-0 px-0"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
              />
            </div>
          </div>

          <div className="px-3 py-3 space-y-3">
            {!query ? (
              <>
                {books.length > 0 && (
                  <div className="space-y-1">
                    <h3 className="text-sm font-medium text-muted-foreground px-2">Books</h3>
                    <div>
                      {books.slice(0, 3).map(book => (
                        <ScrollLink
                          key={book.slug}
                          to={`/books/${book.slug}`}
                          className="flex items-center gap-3 p-2 rounded-md hover:bg-muted"
                          onClick={() => setOpen(false)}
                        >
                          <img 
                            src={book.coverUrl} 
                            alt={book.title}
                            className="w-8 h-12 object-cover rounded"
                          />
                          <div>
                            <div className="font-medium text-sm">{book.title}</div>
                            <div className="text-xs text-muted-foreground line-clamp-1">
                              {book.description}
                            </div>
                          </div>
                        </ScrollLink>
                      ))}
                    </div>
                  </div>
                )}

                {authors.length > 0 && (
                  <div className="space-y-1">
                    <h3 className="text-sm font-medium text-muted-foreground px-2">Authors</h3>
                    <div>
                      {authors.slice(0, 3).map(author => (
                        <ScrollLink
                          key={author.slug}
                          to={`/authors/${author.slug}`}
                          className="flex items-center gap-3 p-2 rounded-md hover:bg-muted"
                          onClick={() => setOpen(false)}
                        >
                          {author.picture && (
                            <img 
                              src={author.picture} 
                              alt={author.name}
                              className="w-8 h-8 object-cover rounded-full"
                            />
                          )}
                          <div className="font-medium text-sm">{author.name}</div>
                        </ScrollLink>
                      ))}
                    </div>
                  </div>
                )}

                {bitcoiners.length > 0 && (
                  <div className="space-y-1">
                    <h3 className="text-sm font-medium text-muted-foreground px-2">Bitcoiners</h3>
                    <div>
                      {bitcoiners.slice(0, 3).map(bitcoiner => (
                        <ScrollLink
                          key={bitcoiner.slug}
                          to={`/bitcoiners/${bitcoiner.slug}`}
                          className="flex items-center gap-3 p-2 rounded-md hover:bg-muted"
                          onClick={() => setOpen(false)}
                        >
                          {bitcoiner.avatarUrl && (
                            <img 
                              src={bitcoiner.avatarUrl} 
                              alt={bitcoiner.name}
                              className="w-8 h-8 object-cover rounded-full"
                            />
                          )}
                          <div className="font-medium text-sm">{bitcoiner.name}</div>
                        </ScrollLink>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {filteredBooks.length > 0 && (
                  <div className="space-y-1">
                    <h3 className="text-sm font-medium text-muted-foreground px-2">Books</h3>
                    <div>
                      {filteredBooks.map(book => (
                        <ScrollLink
                          key={book.slug}
                          to={`/books/${book.slug}`}
                          className="flex items-center gap-3 p-2 rounded-md hover:bg-muted"
                          onClick={() => setOpen(false)}
                        >
                          <img 
                            src={book.coverUrl} 
                            alt={book.title}
                            className="w-8 h-12 object-cover rounded"
                          />
                          <div>
                            <div className="font-medium text-sm">{book.title}</div>
                            <div className="text-xs text-muted-foreground line-clamp-1">
                              {book.description}
                            </div>
                          </div>
                        </ScrollLink>
                      ))}
                    </div>
                  </div>
                )}

                {filteredAuthors.length > 0 && (
                  <div className="space-y-1">
                    <h3 className="text-sm font-medium text-muted-foreground px-2">Authors</h3>
                    <div>
                      {filteredAuthors.map(author => (
                        <ScrollLink
                          key={author.slug}
                          to={`/authors/${author.slug}`}
                          className="flex items-center gap-3 p-2 rounded-md hover:bg-muted"
                          onClick={() => setOpen(false)}
                        >
                          {author.picture && (
                            <img 
                              src={author.picture} 
                              alt={author.name}
                              className="w-8 h-8 object-cover rounded-full"
                            />
                          )}
                          <div className="font-medium text-sm">{author.name}</div>
                        </ScrollLink>
                      ))}
                    </div>
                  </div>
                )}

                {filteredBitcoiners.length > 0 && (
                  <div className="space-y-1">
                    <h3 className="text-sm font-medium text-muted-foreground px-2">Bitcoiners</h3>
                    <div>
                      {filteredBitcoiners.map(bitcoiner => (
                        <ScrollLink
                          key={bitcoiner.slug}
                          to={`/bitcoiners/${bitcoiner.slug}`}
                          className="flex items-center gap-3 p-2 rounded-md hover:bg-muted"
                          onClick={() => setOpen(false)}
                        >
                          {bitcoiner.avatarUrl && (
                            <img 
                              src={bitcoiner.avatarUrl} 
                              alt={bitcoiner.name}
                              className="w-8 h-8 object-cover rounded-full"
                            />
                          )}
                          <div className="font-medium text-sm">{bitcoiner.name}</div>
                        </ScrollLink>
                      ))}
                    </div>
                  </div>
                )}

                {filteredBooks.length === 0 && 
                 filteredAuthors.length === 0 && 
                 filteredBitcoiners.length === 0 && (
                  <div className="text-center py-3 text-muted-foreground text-sm">
                    No results found for "{query}"
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}